import { axios } from '@vyce/core/src/api';

import { CustomCertificate, CustomQualificationRequestData, Image, OrderByItem, Team } from '../../types';
import config from '../config';

import {
  ChangeTeamManagersData,
  CreateTeamData,
  GetCompanyTeamsMembersDataRequest,
  GetCompanyTeamsRequest,
  DeleteMemberRequestDTO,
  AddMemberRequestDTO,
  GetUserData,
  GetAgreementsRequest,
  PaginatedList,
  StatusMessage,
  TeamsSyncSettingsDTO,
  GetSyncSettingsData,
  TeamsDataRequest,
  TeamQualificationWidgetDTO,
  TeamQualificationsWidgetRequest,
  ModuleCandidatatesSchema,
} from '../types';

const URL = `${config.gatewayApiUrl}`;

const generateLogoData = (logo: Image): FormData => {
  const bodyFormData = new FormData();

  if (logo?.file) {
    bodyFormData.append('file', logo.file);
  }

  return bodyFormData;
};

export const getCompanyTeamsRequest = ({ companyId, requestData }: GetCompanyTeamsRequest) => {
  return axios.post(`${URL}/public/v1/teams/companies/${companyId}/search`, requestData);
};

export const createTeamsRequest = (companyId: string, data: CreateTeamData) => {
  return axios.post(`${URL}/public/v1/teams/companies/${companyId}`, data);
};

export const getTeamRequest = (companyId: string, teamId: string) => {
  return axios.get(`${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}`);
};

export const updateTeamRequest = (companyId: string, teamId: string, data: CreateTeamData) => {
  return axios.post(`${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}`, data);
};

export const deleteTeamRequest = (companyId: string, teamId: string) => {
  return axios.delete<StatusMessage>(`${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}`);
};

export const getTeamMembersRequest = (
  companyId: string,
  teamId: string,
  requestData: GetCompanyTeamsMembersDataRequest,
  signal?: AbortSignal
) => {
  return axios.post<PaginatedList<any>>(
    `${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}/members/search`,
    requestData,
    { signal }
  );
};

export const addTeamMembersRequest = (companyId: string, teamId: string, userIds: AddMemberRequestDTO) => {
  return axios.post<StatusMessage>(
    `${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}/members`,
    userIds
  );
};

export const deleteTeamMembersRequest = (
  companyId: string,
  teamId: string,
  data: DeleteMemberRequestDTO
) => {
  return axios.delete<StatusMessage>(
    `${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}/members`,
    {
      data,
    }
  );
};

export const deleteTeamMembersFromModuleRequest = (companyId: string, data: DeleteMemberRequestDTO) => {
  return axios.delete<StatusMessage>(`${URL}/public/v1/teams/companies/${companyId}/members`, {
    data,
  });
};

export const changeTeamManagersRequest = (
  companyId: string,
  teamId: string,
  managers: ChangeTeamManagersData
) => {
  return axios.post<StatusMessage>(
    `${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}/managers`,
    managers
  );
};

export const getAllTeamMembersRequest = (
  companyId: string,
  requestData: GetCompanyTeamsMembersDataRequest
) => {
  return axios.post(`${URL}/public/v1/teams/companies/${companyId}/teams/members/search`, requestData);
};

export const appointTeamManager = ({
  companyId,
  teamId,
  userId,
}: {
  companyId: string;
  teamId: string;
  userId: string;
}) => {
  return axios.post<StatusMessage>(
    `${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}/managers/${userId}`
  );
};

export const deleteTeamManager = ({
  companyId,
  teamId,
  userId,
}: {
  companyId: string;
  teamId: string;
  userId: string;
}) => {
  return axios.delete<StatusMessage>(
    `${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}/managers/${userId}`
  );
};

export const getTeamsManagers = ({
  companyId,
  teamId,
  payload,
}: {
  companyId: string;
  teamId: string;
  payload: {
    order_by: {
      field: string;
      desc: boolean;
    }[];
  };
}) => {
  return axios.post(
    `${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}/managers/search`,
    payload
  );
};

export const getTeamMemberPreviewRequest = ({ userId, companyId }: GetUserData) => {
  return axios.get(`${URL}/public/v1/teams/companies/${companyId}/users/${userId}`);
};

export const getPassportRequest = ({ userId, companyId }: GetUserData) => {
  return axios.get(`${URL}/public/v1/teams/companies/${companyId}/users/${userId}/documents/passport`);
};

export const getRtwRequest = ({ userId, companyId }: GetUserData) => {
  return axios.get(`${URL}/public/v1/teams/companies/${companyId}/users/${userId}/documents/rtw`);
};

export const getHealthAndSafetyPDFByUserIdRequest = ({
  userId,
  companyId,
}: Omit<GetAgreementsRequest, 'token'>) => {
  return axios.post(
    `${URL}/public/v1/teams/companies/${companyId}/users/${userId}/agreements/health_and_safety`,
    {},
    {
      responseType: 'blob',
    }
  );
};

export const getTeamsSyncSettingsRequest = ({ companyId, teamId }: GetSyncSettingsData) => {
  return axios.get<TeamsSyncSettingsDTO>(`${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}/sync_settings`);
};

export const updateTeamsSyncSettingsRequest = ({
  companyId,
  teamId,
  payload,
}: GetSyncSettingsData & {
  payload: TeamsSyncSettingsDTO;
}) => {
  return axios.post<TeamsSyncSettingsDTO>(
    `${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}/sync_settings`,
    payload
  );
};

export const postExportQualifications = ({
  company_id,
  data,
}: {
  company_id: string;
  data: TeamsDataRequest & { order_by?: OrderByItem[] };
}) => {
  return axios.post(`${URL}/public/v1/teams/companies/${company_id}/teams/export_qualifications_to_xlsx`, data, {
    headers: {
      accept: 'application/json',
    },
    responseType: 'blob',
  });
};

export const getTeamQualificationsRequest = (companyId: string) => {
  return axios.post<{ count: number; items: string[] }>(
    `${URL}/public/v1/teams/companies/${companyId}/teams/qualifications`,
    {}
  );
};

export const getTeamsQualifications = ({
  companyId,
  data,
  signal,
}: {
  companyId: string;
  data: TeamQualificationsWidgetRequest;
  signal?: AbortSignal;
}) => {
  return axios.post<{ count: number; items: TeamQualificationWidgetDTO[] }>(
    `${URL}/public/v1/dashboards/widgets/teams/companies/${companyId}/custom/qualifications`,
    data,
    { signal }
  );
};

export const createQualificationRequest = ({
  parameters,
  userId,
  companyId,
}: {
  parameters: {
    scheme_id: string;
    serial_number: string;
    surname: string;
  };
  userId: string;
  companyId: string;
}) => {
  return axios.post(`${URL}/public/v1/teams/companies/${companyId}/users/${userId}/add_qualification`, parameters);
};

export const updateQualificationRequest = ({
  data,
  userId,
  companyId,
  certificateId,
}: {
  data: CustomQualificationRequestData;
  userId: string;
  companyId: string;
  certificateId: string;
}) => {
  return axios.patch(`${URL}/public/v1/teams/companies/${companyId}/users/${userId}/certificates/${certificateId}`, data);
};


export const createCustomQualificationRequest = ({
  data,
  userId,
  companyId,
}: {
  data: CustomQualificationRequestData;
  userId: string;
  companyId: string;
}) => {
  return axios.post<CustomCertificate>(
    `${URL}/public/v1/teams/companies/${companyId}/users/${userId}/add_custom_qualification`,
    data
  );
};

export const uploadCustomQualificationImageRequest = ({
  file,
  userId,
  certificateId,
  companyId,
}: {
  file: File;
  userId: string;
  certificateId: string;
  companyId: string;
}) => {
  const bodyFormData = new FormData();
  bodyFormData.append('file', file);

  return axios.post<CustomCertificate>(
    `${URL}/public/v1/teams/companies/${companyId}/users/${userId}/certificates/${certificateId}/document`,
    bodyFormData
  );
};

export const deleteCustomQualificationImageRequest = ({
  certificateId,
  userId,
  companyId,
  documentId,
}: {
  certificateId: string;
  userId: string;
  companyId: string;
  documentId: string;
}) => {
  return axios.delete<CustomCertificate>(
    `${URL}/public/v1/teams/companies/${companyId}/users/${userId}/certificates/${certificateId}/document/${documentId}`
  );
};

export const getTeamsCandidatesRequest = ({
  companyId,
  teamId,
  payload,
}: GetSyncSettingsData & {
  payload: ModuleCandidatatesSchema;
}) => {
  return axios.post(`${URL}/public/v1/teams/companies/${companyId}/teams/${teamId}/teams_candidates/search`, payload);
};

export const getProximityToWorkRequest = (
  companyId: string,
  payload: {
    team_ids: string[] | null;
    geo_polygon?: string;
    distance_ranges: number[];
    time_distance: number;
    borough_ids?: string[];
    job_titles?: string[];
  }
) => {
  return axios.post(
    `${URL}/public/v1/dashboards/widgets/teams/companies/${companyId}/custom/proximity_to_work`,
    payload
  );
};

export const updateLogoRequest = (companyId: string, team_id: string, logo: Image) => {
  const data = generateLogoData(logo);
  return axios.post(`${URL}/public/v1/teams/companies/${companyId}/teams/${team_id}/logo`, data);
};

export const deleteLogoRequest = (companyId: string, team_id: string) => {
  return axios.delete(`${URL}/public/v1/teams/companies/${companyId}/teams/${team_id}/logo`);
};







